<script setup>
// Opcao da empresa (spec 010, R-24): quando o cartao ou a atividade tem mais de uma pessoa, o evento vai para o
// Google do responsavel (padrao) ou de quem criou. So administrador, e so com o Google Agenda liberado na conta.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter } from 'dashboard/composables/store';
import GoogleCalendarAPI from 'dashboard/api/googleCalendar';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const { isAdmin } = useAdmin();
const getAccount = useMapGetter('accounts/getAccount');
const settings = computed(() => getAccount.value(props.accountId)?.settings);
const visible = computed(
  () => isAdmin.value && settings.value?.google_calendar_enabled === true
);

const OPTIONS = ['assignee', 'creator'];
const eventOwner = ref(
  settings.value?.google_calendar_event_owner || 'assignee'
);
const saving = ref(false);

const save = async value => {
  const previous = eventOwner.value;
  eventOwner.value = value;
  saving.value = true;
  try {
    await GoogleCalendarAPI.updateSettings({ eventOwner: value });
    useAlert(t('GOOGLE_CALENDAR.EVENT_OWNER.SAVED'));
  } catch {
    eventOwner.value = previous;
    useAlert(t('GOOGLE_CALENDAR.EVENT_OWNER.ERROR'));
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <section
    v-if="visible"
    class="p-5 border rounded-xl border-n-weak bg-n-solid-1"
    data-test="google-calendar-event-owner"
  >
    <h3 class="mb-1 text-base font-semibold text-n-slate-12">
      {{ t('GOOGLE_CALENDAR.EVENT_OWNER.TITLE') }}
    </h3>
    <p class="mb-3 text-sm text-n-slate-10">
      {{ t('GOOGLE_CALENDAR.EVENT_OWNER.DESCRIPTION') }}
    </p>
    <div class="flex flex-col gap-2">
      <label
        v-for="option in OPTIONS"
        :key="option"
        class="flex items-center gap-2 text-sm text-n-slate-12"
      >
        <input
          type="radio"
          name="google-calendar-event-owner"
          class="!mb-0"
          :value="option"
          :checked="eventOwner === option"
          :disabled="saving"
          :data-test="`event-owner-${option}`"
          @change="save(option)"
        />
        {{ t(`GOOGLE_CALENDAR.EVENT_OWNER.${option.toUpperCase()}`) }}
      </label>
    </div>
  </section>
</template>
